import { RefObject, useLayoutEffect, useState } from 'react';

import type { SupportMessage } from 'chaika-frontend-types/frontend-cms';

interface Params {
    ticketDetailRef: RefObject<HTMLElement | null>;
    messages: SupportMessage[];
    unreadCount: number;
}

interface Data {
    keepOpeningPosition(): void;
    releaseOpeningPosition(): void;
}

interface OpeningPosition {
    firstUnreadMessageId?: string;
}

export function useOpeningScrollPosition({ ticketDetailRef, messages, unreadCount }: Params): Data {
    const [unreadCountOnOpen] = useState(unreadCount);
    const [openingPosition, setOpeningPosition] = useState<OpeningPosition>();
    const [isKeepingOpeningPosition, setIsKeepingOpeningPosition] = useState(true);

    if (!openingPosition && messages.length > 0) {
        setOpeningPosition({ firstUnreadMessageId: findFirstUnreadMessageId(messages, unreadCountOnOpen) });
    }

    useLayoutEffect(() => {
        if (openingPosition) scrollToOpeningPosition(ticketDetailRef.current, openingPosition);
    }, [openingPosition, ticketDetailRef]);

    return {
        keepOpeningPosition: () => {
            if (openingPosition && isKeepingOpeningPosition) {
                scrollToOpeningPosition(ticketDetailRef.current, openingPosition);
            }
        },
        releaseOpeningPosition: () => setIsKeepingOpeningPosition(false),
    };
}

function findFirstUnreadMessageId(messages: SupportMessage[], unreadCount: number): string | undefined {
    if (unreadCount === 0) return undefined;
    return messages[Math.max(0, messages.length - unreadCount)].id;
}

function scrollToOpeningPosition(ticketDetail: HTMLElement | null, { firstUnreadMessageId }: OpeningPosition): void {
    const messageList = ticketDetail?.querySelector<HTMLElement>('[data-cs-message-list]');
    if (!messageList) return;

    const firstUnreadMessage = firstUnreadMessageId && findMessageElement(messageList, firstUnreadMessageId);
    messageList.scrollTop = firstUnreadMessage
        ? firstUnreadMessage.getBoundingClientRect().top -
          messageList.getBoundingClientRect().top +
          messageList.scrollTop
        : messageList.scrollHeight;
}

function findMessageElement(messageList: HTMLElement, messageId: string): HTMLElement | undefined {
    return Array.from(messageList.querySelectorAll<HTMLElement>('[data-message-id]')).find(
        element => element.dataset.messageId === messageId,
    );
}
